/**
 * Alpine component: certificatesTimeline
 *
 * Timeline animata dei certificati ECM con switch triennio client-side.
 * Tutti i dati vengono iniettati SSR dal controller PHP.
 * Il filtraggio per triennio e il calcolo dei crediti cumulativi
 * avvengono interamente client-side (zero fetch AJAX).
 */
export function registerCertificatesTimeline(Alpine) {
    Alpine.data('certificatesTimeline', (initial) => ({
        // All certificates (unfiltered, from SSR)
        allCertificates: initial.allCertificates || [],

        // Trienni available (sorted desc)
        trienni: initial.trienni || [],

        // Currently selected triennio [2023, 2024, 2025]
        currentTriennio: initial.currentTriennio || null,

        // Computed: filtered + cumulative
        filteredCerts: [],
        totalCredits: 0,

        // Animation state
        animated: false,
        displayCredits: 0,

        init() {
            this.applyTriennio(this.currentTriennio);

            // Trigger animation after paint
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.animated = true;
                    this.animateCredits(this.totalCredits);
                });
            });
        },

        /**
         * Switch triennio: filter, compute cumulative, restart animations.
         */
        selectTriennio(triennio) {
            if (this.currentTriennio && this.currentTriennio[0] === triennio[0]) return;

            // Reset animations
            this.animated = false;
            this.displayCredits = 0;
            this.currentTriennio = triennio;

            this.applyTriennio(triennio);

            // Re-trigger animations after DOM update
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.animated = true;
                    this.animateCredits(this.totalCredits);
                });
            });
        },

        /**
         * Filter certificates by triennio and compute cumulative credits.
         */
        applyTriennio(triennio) {
            if (!triennio) {
                this.filteredCerts = [];
                this.totalCredits = 0;
                return;
            }

            // Filter by year range
            var filtered = this.allCertificates.filter(function (c) {
                if (!c.date) return false;
                var year = parseInt(c.date.substring(0, 4), 10);
                return year >= triennio[0] && year <= triennio[2];
            });

            // Sort ascending by date (oldest first) to compute cumulative
            var ascending = filtered.slice().sort(function (a, b) {
                return (a.date || '').localeCompare(b.date || '');
            });

            var running = 0;
            ascending.forEach(function (c) {
                running += (c.credits || 0);
                c.cumulative = Math.round(running * 10) / 10;
            });

            // Back to descending (most recent first)
            this.filteredCerts = ascending.reverse();
            this.totalCredits = Math.round(running * 10) / 10;
        },

        /**
         * Animate the total credits counter with ease-out exponential.
         */
        animateCredits(target) {
            var self = this;

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                self.displayCredits = target;
                return;
            }

            var duration = 1200;
            var start = performance.now();

            function step(now) {
                var elapsed = now - start;
                var progress = Math.min(elapsed / duration, 1);
                // Ease-out exponential
                var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
                self.displayCredits = Math.round(target * eased * 10) / 10;
                if (progress < 1) {
                    requestAnimationFrame(step);
                } else {
                    self.displayCredits = target;
                }
            }

            requestAnimationFrame(step);
        },

        /**
         * Format date to Italian locale: "12 mar 2025"
         */
        formatDate(dateStr) {
            if (!dateStr) return '';
            var d = new Date(dateStr.substring(0, 10) + 'T00:00:00');
            return d.toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            });
        },

        /**
         * Format credits number: integer if whole, one decimal otherwise.
         */
        formatCredits(val) {
            if (val == null) return '0';
            var n = parseFloat(val);
            return Number.isInteger(n) ? n.toString() : n.toFixed(1).replace('.', ',');
        },

        /**
         * Triennio label: "2023 - 2025"
         */
        triennioLabel(t) {
            return t[0] + ' - ' + t[2];
        },

        /**
         * Is this the currently selected triennio?
         */
        isCurrentTriennio(t) {
            return this.currentTriennio && this.currentTriennio[0] === t[0];
        },

        /**
         * Is this triennio the one containing the current calendar year?
         */
        isRealCurrentTriennio(t) {
            var now = new Date().getFullYear();
            return t.indexOf(now) !== -1;
        },
    }));
}
